import { FC } from 'react'

import { Spinner } from '@factorialco/deprecated-design-system'
import {
  BulkCreateBenefitsHealthcareEnrollmentDocument,
  BulkCreateBenefitsHealthcareEnrollmentNewProfilesInput,
  HealthcareEnrollmentWizardDocument,
  MyBenefitsListPageDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { closeModal } from 'modules/benefits/components/benefitModals/actions'
import { useBenefitModals } from 'modules/benefits/components/benefitModals/useBenefitModals'

import { supportedIdentifierTypes } from './constants'
import HealthcareEnrollmentWizard from './index'
import {
  BenefitMemberEnrollmentData,
  PersonalInformationFormData,
} from './types'
import {
  hasMissingEmployeeAddressData,
  normalizeHiddenEmployeeAddressFields,
  personalInformationStepSchema,
} from './validation'

const HealthcareEnrollmentWizardContainer: FC = () => {
  const { dispatch } = useBenefitModals()
  const {
    company: { id: companyId },
  } = useCurrentSession()

  const { data, loading } = useQuery(HealthcareEnrollmentWizardDocument, {
    variables: {
      companyId,
    },
  })

  const employee = data?.apiCore?.currentsConnection?.nodes?.[0]?.employee

  // Hardcoded fallback to Spain for now. Employees in benefits should already have a country in their profile.
  // We fall back to 'es' for any country not yet listed in supportedIdentifierTypes (only 'es' is supported today).
  const employeeCountry = loading ? null : employee?.country || 'es'
  const identifierTypes = employeeCountry
    ? (supportedIdentifierTypes[employeeCountry] ?? supportedIdentifierTypes['es'])
    : []

  const [bulkCreateHealthcareEnrollment] = useMutation(
    BulkCreateBenefitsHealthcareEnrollmentDocument,
    {
      extractorFn: (createdData) =>
        createdData.benefitsMutations?.bulkCreateBenefitsHealthcareEnrollment,
      refetchQueries: [
        {
          query: MyBenefitsListPageDocument,
          variables: { companyId, employeeId: employee?.id },
        },
      ],
    }
  )

  const hasIbanFormat = employee?.bankNumberFormat === 'iban'
  const initialPersonalInformationData: PersonalInformationFormData = {
    firstName: employee?.firstName,
    lastName: employee?.lastName,
    email: employee?.email ?? undefined,
    phoneNumber: employee?.phoneNumber ?? undefined,
    addressLine1: employee?.addressLine1 ?? undefined,
    addressLine2: employee?.addressLine2 ?? undefined,
    birthday: employee?.birthdayOn ?? undefined,
    postalCode: employee?.postalCode ?? undefined,
    city: employee?.city ?? undefined,
    province: employee?.state ?? undefined,
    taxIdNumber: employee?.identifier ?? undefined,
    taxIdType: employee?.identifierType ?? undefined,
    iban: hasIbanFormat ? (employee?.bankNumber ?? undefined) : undefined,
    sex:
      typeof employee?.gender === 'string'
        ? employee.gender === 'female'
          ? 'female'
          : 'male'
        : undefined,
    memberType: 'employee',
  }

  const plans = data?.benefits?.healthcarePlansConnection?.nodes
  const plan = plans?.[0]

  // Don't render the wizard while we are loading the data
  if (loading) {
    return <Spinner />
  }

  //We should have a plan in order to start the enrollment
  if (!plan || !employee) {
    return null
  }

  const handleClose = () => {
    track(trackingEvents.benefits.closeHealthcareEnrollmentWizard)
    dispatch(closeModal())
  }

  const handleSubmit = async (enrollmentData: BenefitMemberEnrollmentData) => {
    track(trackingEvents.benefits.submitHealthcareEnrollmentWizard)

    const showEmployeeAddressFields = hasMissingEmployeeAddressData(initialPersonalInformationData)

    // Validate the personal information data before proceeding
    const validationResult = personalInformationStepSchema(showEmployeeAddressFields).safeParse(
      normalizeHiddenEmployeeAddressFields(enrollmentData)
    )
    if (!validationResult.success) {
      const error = new Error(
        `Healthcare enrollment validation failed: ${JSON.stringify(validationResult.error.errors)}`
      )
      error.name = 'HealthcareEnrollmentValidationError'
      throw error
    }

    const validatedData = {
      ...validationResult.data,
      addressLine2: validationResult.data.addressLine2 || '',
      ...(showEmployeeAddressFields
        ? {
            postalCode:
              validationResult.data.postalCode || initialPersonalInformationData.postalCode || '',
            city: validationResult.data.city || initialPersonalInformationData.city || '',
            province: validationResult.data.province || initialPersonalInformationData.province || '',
          }
        : {
            postalCode: initialPersonalInformationData.postalCode || '',
            city: initialPersonalInformationData.city || '',
            province: initialPersonalInformationData.province || '',
          }),
    } satisfies BenefitMemberEnrollmentData

    const newProfiles: BulkCreateBenefitsHealthcareEnrollmentNewProfilesInput[] = [
      {
        employeeId: employee.id,
        companyId,
        ...validatedData,
      },
    ]
    const result = await bulkCreateHealthcareEnrollment({
      variables: {
        companyId,
        employeeId: employee.id,
        healthcarePlanId: plan.id,
        newProfiles,
        existingProfiles: [],
      },
    })

    const handled = handleResult(result, (errors) => {
      const errorMessages = Object.values(errors)
      if (errorMessages.length > 0) {
        app.setFlash({
          type: 'wrong',
          message: errorMessages.join(', '),
          time: 5000,
        })
      }
    })

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('benefits.healthcare.enrollment_wizard.create_success'),
        time: 3000,
      })
      dispatch(closeModal())
    }
  }

  return (
    <HealthcareEnrollmentWizard
      healthcarePlanData={plan}
      initialPersonalInformationData={initialPersonalInformationData}
      identifierTypes={identifierTypes || []}
      onSubmit={handleSubmit}
      onClose={handleClose}
    />
  )
}

export default HealthcareEnrollmentWizardContainer
